import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useModules } from '../contexts/ModulesContext';
import { usePermissions } from '../contexts/PermissionsContext';
import * as LucideIcons from 'lucide-react';
import api from '../services/api';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { modules } = useModules();
  const { canView } = usePermissions();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get('/messages/inbox/unread-count').then(r => setUnread(r.data.count)).catch(() => {});
  }, []);

  const getIcon = (name) => LucideIcons[name] || LucideIcons.Box;

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">{t('dashboard.welcome', { name: user?.name })}</h1>
        <p className="text-gray-500 mt-1">{t('dashboard.chooseModule')}</p>
      </div>

      {/* Unread messages banner */}
      {unread > 0 && (
        <button onClick={() => navigate('/messages')}
          className="w-full mb-6 flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition text-left">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
            <LucideIcons.Mail size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <span className="font-medium text-blue-900">
              {t('dashboard.unreadMessages', { count: unread })}
            </span>
            <p className="text-sm text-blue-600 mt-0.5">{t('dashboard.clickToMessages')}</p>
          </div>
          <span className="px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded-full">{unread}</span>
        </button>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {modules.filter(m => canView(m.key)).map(mod => {
          const Icon = getIcon(mod.icon);
          return (
            <button key={mod.key} onClick={() => navigate(mod.path)}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-gray-300 transition text-left group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition"
                style={{ backgroundColor: `${mod.color}15` }}>
                <Icon size={24} style={{ color: mod.color }} />
              </div>
              <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition">{mod.name}</h3>
              {mod.description && <p className="text-sm text-gray-500 mt-1">{mod.description}</p>}
            </button>
          );
        })}
      </div>

      {modules.filter(m => canView(m.key)).length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <LucideIcons.PackageOpen size={48} className="mx-auto mb-4" />
          <p>{t('dashboard.noModules')}</p>
        </div>
      )}
    </div>
  );
}
